import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { sendPayment, getCheckoutDetails } from "../../utils/api/payment";
import { changeAmountFormat } from "../../utils/format";
import KNetLogo from "../../assets/images/knet.png";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ContractCard from "./ContractCard";
import Stack from "@mui/material/Stack";
import { useFormik } from "formik";
import * as Yup from "yup";
import Alert from "@mui/material/Alert";
import CheckIcon from "@mui/icons-material/Check";
import { Divider } from "@mui/material";
import { Header } from "../navbar/Header";
import Person from "@mui/icons-material/Person";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";

function Checkout() {
  const { unique_payment_identifier } = useParams(); // Get unique_payment_identifier from url
  const [isSubmitting, setIsSubmitting] = useState(false); // New state for tracking form submission
  const [totalAmount, setTotalAmount] = useState(0); // New state for tracking total amount
  const [subTotalAmount, setSubTotalAmount] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  // Apple Pay button is rendered only when the browser advertises support AND
  // the selected gateway is Hesabe. MyFatoorah does not support Apple Pay.
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.ApplePaySession &&
      typeof window.ApplePaySession.canMakePayments === "function"
    ) {
      try {
        setApplePayAvailable(window.ApplePaySession.canMakePayments());
      } catch (e) {
        setApplePayAvailable(false);
      }
    }
  }, []);

  // Retreive Checkout Items details from backend
  const { data: checkoutItems, isLoading: isCheckoutItemsLoading } = useQuery(
    ["CheckoutItems", unique_payment_identifier],
    () => getCheckoutDetails(unique_payment_identifier)
  );

  const totalOutstandingAmount = checkoutItems?.data?.contracts?.reduce(
    (acc, contract) => acc + contract.total_pending_amount,
    0
  );

  // Mirror the backend gateway-selection logic so we know which gateway will
  // be used for the current total. Derived (not state) so it re-evaluates on
  // every render as the user selects/deselects invoices.
  const gatewayConfig = checkoutItems?.data?.gateway_config;
  const usingMyfatoorah =
    !!gatewayConfig?.myfatoorah_enabled &&
    totalAmount >= parseFloat(gatewayConfig?.myfatoorah_threshold_kwd ?? "999999");

  // On mount, select all invoices and calculate the initial total amount
  useEffect(() => {
    if (!checkoutItems) return; // Return if checkoutItems is not available

    // Select all invoices by default
    const allInvoicesIds = checkoutItems?.data?.contracts.flatMap((contract) =>
      contract.invoices.map((invoice) => invoice.id)
    );
    setSelectedInvoices(allInvoicesIds);

    // Calculate the initial total amount
    let initialSubTotal = 0;
    let initialDiscount = 0;
    let payableAmount = 0;

    checkoutItems?.data?.contracts.forEach((contract) => {
      contract.invoices.forEach((invoice) => {
        initialSubTotal += Number(invoice.invoice_amount);
        initialDiscount += Number(invoice.discount_value || 0);
        payableAmount += Number(invoice.remaining_amount);
      });
    });

    setSubTotalAmount(initialSubTotal);
    setTotalDiscount(initialDiscount);
    setTotalAmount(payableAmount);
  }, [checkoutItems]);

  // On every change in selectedInvoices, recalculate the total amount
  useEffect(() => {
    if (!checkoutItems) return; // Return if checkoutItems is not available

    // Calculate the new total amount based on the selected invoices
    let subTotalAmount = 0;
    let totalDiscount = 0;
    let payableAmount = 0;

    checkoutItems?.data?.contracts.forEach((contract) => {
      contract.invoices.forEach((invoice) => {
        if (selectedInvoices.includes(invoice.id)) {
          subTotalAmount += Number(invoice.invoice_amount);
          totalDiscount += Number(invoice.discount_value || 0);
          payableAmount += Number(invoice.remaining_amount);
        }
      });
    });

    setSubTotalAmount(subTotalAmount);
    setTotalDiscount(totalDiscount);
    setTotalAmount(payableAmount);
  }, [selectedInvoices, checkoutItems]);

  // Handle checkbox changes
  const handleCheckboxChange = (invoiceId, isChecked, contract) => {
    // Helper function to get all invoices for a given month
    const getInvoicesForMonth = (month, year) => {
      return contract.invoices
        .filter((invoice) => {
          const dueDate = new Date(invoice.due_date);
          return dueDate.getMonth() === month && dueDate.getFullYear() === year;
        })
        .map((invoice) => invoice.id);
    };

    // Helper function to get all unpaid invoices before a given month
    const getUnpaidInvoicesBeforeMonth = (month, year) => {
      return contract.invoices
        .filter((invoice) => {
          const dueDate = new Date(invoice.due_date);
          return (
            dueDate.getFullYear() < year ||
            (dueDate.getFullYear() === year && dueDate.getMonth() < month)
          );
        })
        .map((invoice) => invoice.id);
    };

    // Helper function to get all unpaid invoices from a given month onwards
    const getUnpaidInvoicesFromMonthOnward = (month, year) => {
      return contract.invoices
        .filter((invoice) => {
          const dueDate = new Date(invoice.due_date);
          return (
            dueDate.getFullYear() > year ||
            (dueDate.getFullYear() === year && dueDate.getMonth() >= month)
          );
        })
        .map((invoice) => invoice.id);
    };

    // Get the details of the current invoice
    const currentInvoice = contract.invoices.find(
      (invoice) => invoice.id === invoiceId
    );
    const currentInvoiceDueDate = new Date(currentInvoice.due_date);
    const currentMonth = currentInvoiceDueDate.getMonth();
    const currentYear = currentInvoiceDueDate.getFullYear();

    // Get all invoices for the current month
    const currentMonthInvoices = getInvoicesForMonth(currentMonth, currentYear);

    // Get all unpaid invoices before the current month
    const unpaidInvoicesBeforeCurrentMonth = getUnpaidInvoicesBeforeMonth(
      currentMonth,
      currentYear
    );

    // Get all unpaid invoices from the current month onwards
    const unpaidInvoicesFromCurrentMonthOnward =
      getUnpaidInvoicesFromMonthOnward(currentMonth, currentYear);

    if (isChecked) {
      // If checked, add all invoices for the current month and unpaid invoices before the current month
      setSelectedInvoices((prev) => [
        ...new Set([
          ...prev,
          ...currentMonthInvoices,
          ...unpaidInvoicesBeforeCurrentMonth,
        ]),
      ]);
    } else {
      // If unchecked, remove all invoices for the current month and onward
      setSelectedInvoices((prev) =>
        prev.filter((id) => !unpaidInvoicesFromCurrentMonthOnward.includes(id))
      );
    }
  };

  // Below variable to show Contract Card and under each Contract Card, show Invoice Card embedded
  const contractsList = checkoutItems?.data?.contracts.map((contract) => {
    // Show Contract Card only if there are pending invoices
    if (contract.total_pending_amount > 0) {
      return (
        <ContractCard
          key={contract.id}
          contract={contract}
          handleCheckboxChange={handleCheckboxChange}
          selectedInvoices={selectedInvoices}
        />
      );
    }
  });

  const formik = useFormik({
    initialValues: {
      amount: totalAmount ? parseFloat(totalAmount.toString()).toFixed(3) : "0",
      paymentType: 1, // Knet = 1, Credit Card = 2
      orderReferenceNumber: "",
      variable1: "",
      variable2: `Tenant__#${checkoutItems?.data?.tenant.user.id}-${checkoutItems?.data?.tenant.user.english_name}`,
      variable3: "",
      variable4: "",
      variable5: "",
      invoices: selectedInvoices,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      amount: Yup.number().moreThan(0, "Amount must be greater than KD 0"),
      invoices: Yup.array()
        .of(Yup.number())
        .min(1, "Select at least one invoice"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true); // Set isSubmitting to true when submission starts

      const updatedValues = {
        ...values,
        invoices: selectedInvoices,
        variable1: `ContractIDs__${checkoutItems?.data?.contracts
          .map((contract) => `#${contract.id}`)
          .join("_")}`,
        variable3: `Building__${checkoutItems?.data?.contracts
          .map((contract) =>
            contract.unit.property_fk.name.replace(/\s+/g, "_")
          )
          .join("_")}`,
      };

      try {
        // Make the API call to submit the payment data
        const response = await sendPayment(updatedValues);

        // Redirect to the payment link, if provided in the response
        if (response.data && response.data.payment_url) {
          redirectToPaymentLink(response.data.payment_url);
        } else {
          // Handle case where the payment URL is not provided
          console.error("Payment URL not provided in response");
        }
      } catch (error) {
        console.error("Error sending payment:", error);
        toast.error("Please try again later.");
        setIsSubmitting(false); // Set isSubmitting to false when submission on failure
      }
    },
  });

  function redirectToPaymentLink(link) {
    window.location.href = link;
  }

  // Submit the form with a specific Hesabe paymentType.
  //   1  = KNet (default Pay Now button)
  //   11 = KNET Debit Apple Pay (Apple Pay button)
  // See Hesabe direct integration docs for the full paymentType table.
  async function submitWithPaymentType(paymentType) {
    await formik.setFieldValue("paymentType", paymentType);
    formik.submitForm();
  }

  return (
    <div className="min-h-[100vh] bg-[#F7F6F2]">
      <Header uid={unique_payment_identifier} />

      <form onSubmit={formik.handleSubmit}>
        <header className="bg-transparent">
          <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8 flex flex-col justify-between">
            <div className="flex flex-row py-3 justify-between items-center">
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                Checkout
              </h1>
            </div>

            {/* Tenant Details */}
            <div className="m-1 space-y-4">
              <Card
                sx={{
                  minWidth: 275,
                  display: "flex",
                  justifyContent: "left",
                  alignItems: "center",
                  // padding: "1rem",
                }}
              >
                <Person fontSize="large" sx={{ ml: 2, mr: 2 }} />
                <CardContent>
                  <Typography
                    sx={{ fontSize: 14 }}
                    color="text.secondary"
                    gutterBottom
                  >
                    Name
                  </Typography>
                  <Typography variant="h5" component="div">
                    {checkoutItems?.data?.tenant.user.company_name_ar ||
                      checkoutItems?.data?.tenant.user.company_name_en ||
                      checkoutItems?.data?.tenant.user.arabic_name ||
                      checkoutItems?.data?.tenant.user.english_name}
                  </Typography>
                </CardContent>
              </Card>
            </div>

            <div className="flex-col pt-3 justify-between items-center">
              {totalOutstandingAmount == 0 ? (
                <Alert
                  sx={{ mb: 2 }}
                  icon={<CheckIcon fontSize="inherit" />}
                  severity="success"
                >
                  No invoices to pay
                </Alert>
              ) : (
                <>
                  <Alert
                    sx={{ mb: 2 }}
                    icon={<CheckIcon fontSize="inherit" />}
                    severity="success"
                  >
                    Please select the invoices you want to pay
                  </Alert>

                  {isCheckoutItemsLoading || (
                    <h1 className="text-xl font-bold tracking-tight text-gray-900">
                      Contracts
                    </h1>
                  )}
                </>
              )}
            </div>
            {/* Contract Card */}
            {isCheckoutItemsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <div className="m-3 space-y-4">{contractsList}</div>
            )}
            {/* <div className="m-3 space-y-4">{contractsList}</div> */}
          </div>
        </header>
        {/* Invoice Summary Fixed Buttom Section */}
        <Divider variant="middle" />
        <div className="mx-auto max-w-7xl px-4 pt-2 sm:px-6 lg:px-8 flex flex-col justify-between">
          <div className="flex-col py-2 justify-between items-center">
            <h1 className="text-xl font-bold tracking-tight text-gray-900 text-center">
              Payment Summary
            </h1>
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-between">
          <div className="m-2">
            <Box>
              <Card variant="solid">
                <CardContent>
                  <Stack direction="column" spacing={2} sx={{ px: 3 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h7">Subtotal</Typography>
                      <Typography variant="h7">
                        KD {changeAmountFormat(subTotalAmount)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h7">Discount</Typography>
                      <Typography variant="h7">
                        KD {changeAmountFormat(totalDiscount)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h7">Fees</Typography>
                      <Typography variant="h7">KD 0.000</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h6" style={{ fontWeight: "bold" }}>
                        Total
                      </Typography>
                      <Typography variant="h6" style={{ fontWeight: "bold" }}>
                        KD {changeAmountFormat(totalAmount)}
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </div>
        </div>
        {/* Payment Button */}
        <div className="mx-auto max-w-sm px-6 sm:px-6 lg:px-8 flex flex-col justify-between space-y-3 my-5">
          <div className="flex flex-col items-center mx-auto w-full space-y-3">
            {/* KNet button (paymentType=1) */}
            <button
              type="button"
              onClick={() => submitWithPaymentType(1)}
              className={`rounded-md px-7 py-3 w-full text-xl font-extrabold transition duration-200
            ${
              totalAmount > 0
                ? "bg-primary hover:bg-[52555C] hover:opacity-80 active:bg-[52555C] text-white"
                : "bg-gray-400 text-gray-500 cursor-not-allowed"
            }`}
              disabled={
                isCheckoutItemsLoading ||
                totalAmount <= 0 ||
                isSubmitting ||
                totalOutstandingAmount == 0 ||
                checkoutItems?.status != 200
              }
            >
              {isSubmitting ? (
                <span>Redirecting...</span>
              ) : (
                <div className="flex items-center justify-center space-x-5">
                  <img src={KNetLogo} className={`w-10`} alt="KNet" />
                  <span>Pay Now!</span>
                </div>
              )}
            </button>

            {/* Apple Pay button — only rendered when the browser supports it.
                Uses paymentType=11 (KNET Debit Apple Pay) per Hesabe direct
                integration. The customer authenticates via Touch/Face ID on
                Hesabe's hosted payment page. */}
            {applePayAvailable && !usingMyfatoorah && (
              <button
                type="button"
                onClick={() => submitWithPaymentType(11)}
                className={`rounded-md px-7 py-3 w-full text-xl font-extrabold transition duration-200
              ${
                totalAmount > 0
                  ? "bg-black hover:opacity-80 active:opacity-70 text-white"
                  : "bg-gray-400 text-gray-500 cursor-not-allowed"
              }`}
                disabled={
                  isCheckoutItemsLoading ||
                  totalAmount <= 0 ||
                  isSubmitting ||
                  totalOutstandingAmount == 0 ||
                  checkoutItems?.status != 200
                }
                aria-label="Pay with Apple Pay"
              >
                {isSubmitting ? (
                  <span>Redirecting...</span>
                ) : (
                  <div className="flex items-center justify-center space-x-3">
                    {/* Apple logo (SVG) + "Pay" — minimalist Apple Pay button.
                        Full Apple Pay button assets can be substituted later. */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 384 512"
                      className="w-6 h-6 fill-current"
                      aria-hidden="true"
                    >
                      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                    </svg>
                    <span>Pay</span>
                  </div>
                )}
              </button>
            )}

            {formik.touched.invoices && formik.errors.invoices ? (
              <div className="error">{formik.errors.invoices}</div>
            ) : null}
          </div>
          {/* <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button startDecorator={<CircularProgress variant="solid" />}>
              Loading…
            </Button>
          </Box> */}
        </div>
      </form>
    </div>
  );
}

export default Checkout;
